<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.container"
  >
    <StepIndicatorPrimary
      :nowrap="true"
      :stepIndex="stepIndex"
      :steps="steps"
      :style="style.stepIndicator"
    />
    <div :style="getSkillNameStyle">
      <HeaderMediumSecondary>
        What is the skill name?
      </HeaderMediumSecondary>
      <TextHintPrimary :style="style.skillNameHint">
        HINT: Use a well-known, concise name without symbols.
      </TextHintPrimary>
      <InputSkillPrimary
        :disabled="disableInputSkillName"
        :disableValidator="true"
        placeholder="Type skill name here"
        v-model="form.data.skillName"
        @change-input="updateFormData"
      />
    </div>
    <div :style="getSkillStatusTypeStyle">
      <HeaderMediumSecondary :style="style.skillStatusTypeHeader">
        Are you currently using the {{ form.data.skillName }}?
      </HeaderMediumSecondary>
      <DialogueOptionPrimary
        :disableDeselect="true"
        idKey="id"
        labelKey="name"
        :options="skillStatusTypes"
        v-model="form.data.skillStatusTypeId"
        @change="updateFormData"
        @select="$emit('step-forward')"
      />
    </div>
    <div :style="getPeriodCategoryStyle">
      <HeaderMediumSecondary :style="style.periodCategoryHeader">
        How long is your experience with {{ form.data.skillName }}?
      </HeaderMediumSecondary>
      <DialogueOptionPrimary
        :disableDeselect="true"
        idKey="id"
        labelKey="name"
        :options="periodCategories"
        v-model="form.data.periodCategoryId"
        @change="updateFormData"
        @select="$emit('step-forward')"
      />
    </div>
    <div :style="getTargetsStyle">
      <HeaderMediumSecondary>
        Is {{ form.data.skillName }} related to any of your existing skills?
      </HeaderMediumSecondary>
      <TextHintPrimary :style="style.targetsHint">
        Select all that apply.
      </TextHintPrimary>
      <TagToggleListPrimary
        v-if="linkTargetTenantUserSkills.length > 0"
        v-model="form.data.targetIds"
        :items="linkItems"
        @change="updateFormData"
        :style="style.linkTargets"
      />
      <TextMutedPrimary v-else>
        You have no skill to link yet.
      </TextMutedPrimary>
    </div>
    <div :style="getPreviewStyle">
      <CardAttention
        :style="style.cardAttention"
      >
        <HeaderMediumAttention>
          Congratulations!
        </HeaderMediumAttention>
        <TextSmallAttention>
          {{ form.data.skillName }} has successfully been added to your skills graph.
        </TextSmallAttention>
      </CardAttention>
      <FormTenantUserSkillPrimary
        :freeze="true"
        :form="form"
        :tenantUserSkills="tenantUserSkills"
      />
    </div>
  </div>
</template>

<script>
  import CardAttention from '@modules/v2/card/card-attention'
  import DialogueOptionPrimary
    from '@modules/v2/dialogue-option/dialogue-option-primary'
  import FormTenantUserSkillPrimary
    from '@modules/v2/form/form-tenant-user-skill-primary'
  import HeaderMediumAttention from '@modules/v2/header/header-medium-attention'
  import HeaderMediumSecondary from '@modules/v2/header/header-medium-secondary'
  import InputSkillPrimary from '@modules/v2/input/input-skill-primary'
  import StepIndicatorPrimary
    from '@modules/v2/step-indicator/step-indicator-primary'
  import TagToggleListPrimary from '@modules/v2/tag/tag-toggle-list-primary'
  import TextHintPrimary from '@modules/v2/text/text-hint-primary'
  import TextMutedPrimary from '@modules/v2/text/text-muted-primary'
  import TextSmallAttention from '@modules/v2/text/text-small-attention'
  import { Layout } from '@js/styles'
  import { mapState } from 'vuex'

  const stepToStepIndex = {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
    4: 3,
  };

  const steps = [
    {
      label: 'Enter name',
      nodeLabel: '1',
    },
    {
      label: 'Add details',
      nodeLabel: '2',
    },
    {
      label: 'Link',
      nodeLabel: '3',
    },
    {
      label: 'Done!',
      nodeLabel: '4',
    },
  ];

  export default {
    name: 'FormTenantUserSkillSecondary',
    inheritAttrs: false,
    components: {
      CardAttention,
      DialogueOptionPrimary,
      FormTenantUserSkillPrimary,
      HeaderMediumAttention,
      HeaderMediumSecondary,
      InputSkillPrimary,
      StepIndicatorPrimary,
      TagToggleListPrimary,
      TextHintPrimary,
      TextMutedPrimary,
      TextSmallAttention,
    },
    props: {
      disableInputSkillName: {
        type: Boolean,
        required: false,
        default: false,
      },
      form: {
        type: Object,
        required: false,
        default: () => {},
      },
      step: {
        type: Number,
        required: false,
        default: 0,
      },
      tenantUserSkills: {
        type: Array,
        required: false,
        default: () => [],
      },
    },
    data: function () {
      return {
        stepToStepIndex: stepToStepIndex,
        steps: steps,
        style: {
          cardAttention: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            margin: `40px 0 ${Layout.space.lg} 0`,
            maxHeight: '108px',
            minHeight: '80px',
            overflowY: 'hidden',
            padding: Layout.space.md,
          },
          container: {
            height: '100%',
            padding: `${Layout.space.lg} 0`,
            width: '100%',
          },
          linkTargets: {
            maxHeight: '204px',
            minHeight: Layout.space.md,
            overflowX: 'hidden',
            overflowY: 'scroll',
          },
          periodCategoryHeader: {
            margin: `0 0 ${Layout.space.md} 0`,
          },
          skillNameHint: {
            margin: `${Layout.space.sm} 0 ${Layout.space.md}`,
          },
          skillStatusTypeHeader: {
            margin: `0 0 ${Layout.space.md} 0`,
          },
          stepIndicator: {
            alignItems: 'center',
            display: 'flex',
            padding: `0 ${Layout.space.lg}`,
          },
          targetsHint: {
            margin: `${Layout.space.sm} 0 ${Layout.space.md}`,
          },
        },
      };
    },
    computed: {
      ...mapState({
        periodCategories: state =>
          state.globalVariables.data.experiencePeriodCategories,
        skillStatusTypes: state =>
          state.globalVariables.data.skillStatusTypes,
      }),
      getSkillNameStyle: function () {
        return {
          display: this.step === 0 ? 'block' : 'none',
          margin: '112px 0 0 0',
        };
      },
      getSkillStatusTypeStyle: function () {
        return {
          display: this.step === 1 ? 'block' : 'none',
          margin: '112px 0 0 0',
        };
      },
      getPeriodCategoryStyle: function () {
        return {
          display: this.step === 2 ? 'block' : 'none',
          margin: '112px 0 0 0',
        };
      },
      getTargetsStyle: function () {
        return {
          display: this.step === 3 ? 'block' : 'none',
          margin: '112px 0 0 0',
        };
      },
      getPreviewStyle: function () {
        return {
          display: this.step === 4 ? 'block' : 'none',
        };
      },
      linkItems: function () {
        return this.linkTargetTenantUserSkills
          .reduce((result, elem) => {
            result.push({
              'id': elem.id,
              'label': elem.skill.name,
            });
            return result;
          }, []);
      },
      linkTargetTenantUserSkills: function () {
        return this.tenantUserSkills
          .filter(
            elem => (
              elem.skill.name.trim().toLowerCase() !==
                this.form.data.skillName.trim().toLowerCase()
            )
          );
      },
      stepIndex: function () {
        return this.stepToStepIndex[this.step];
      },
    },
    methods: {
      updateFormData: function () {
        this.$emit('update:form', this.form);
      },
    },
    watch: {
      form: {
        handler: function (newVal, oldVal) {
          this.form = newVal;
        },
        deep: true,
      },
    },
  };

  export {
    stepToStepIndex,
    steps,
  };
</script>
